<template>
  <div class="dropdown" ref="dropdown">
    <div class="dropdown__toggler" @click="showDropdown = true" ref="toggler" :class="{
      'dropdown__toggler--active': showDropdown,
    }"></div>

    <div class="dropdown__dropdown" v-show="showDropdown" ref="dropdownEl" :style="{ left: `${x}px`, top: `${y}px` }">
      <template v-if="showDropdown">
        <Loading v-if="servicesLoading" />
        <div v-else class="dropdown__content">
          <div class="dropdown__title">{{ $t("addPolicyModal.chooseServiceTitle") }}</div>

          <div class="dropdown__search">
            <input type="text" class="input input--default input--search"
              :placeholder="$t(`addPolicyModal.${'searchServices'}`)" v-model="serviceSearchQuery" />
          </div>

          <div class="dropdown__body">
            <div v-if="!filteredServices.length" class="dropdown__no-data">
              {{ $t("addPolicyModal.noServicesFound") }}
            </div>

            <template v-else>
              <div class="dropdown__item" v-for="service in filteredServices" :key="service.id">
                <div class="checkbox">
                  <input type="checkbox" class="checkbox__checkbox" :checked="services.some((s) => s.id === service.id)"
                    :id="service.id" @input="addOrRemoveServices($event, service)" />
                  <label :for="service.id" class="checkbox__label checkbox__label--smaller"></label>
                </div>
                <label :for="service.id" class="dropdown__item-title">
                  {{ service[`long_${language}`] }}
                </label>
              </div>
            </template>
          </div>

          <div class="dropdown__buttons">
            <button class="dropdown__button dropdown__button--submit" @click="applyServices()"></button>
            <button class="dropdown__button dropdown__button--cancel" @click="close()"></button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useStore } from "vuex";
import Loading from "@/partials/Loading";

const props = defineProps({
  dropdownServices: {
    required: true,
    type: Array,
  },
  servicesLoading: {
    required: false,
    type: Boolean,
  },
  services: {
    required: false,
    type: Array,
  },
  addOrRemoveServices: {
    required: true,
    type: Function,
  },
  applyServices: {
    required: true,
    type: Function,
  },
  closeServicesDropdown: {
    required: true,
    type: Function,
  },
});

const store = useStore();

const showDropdown = ref(false);
const x = ref(0);
const y = ref(0);
const rect = ref(null);
const isOutside = ref(false);

// Template refs - these names must match the ref attributes in the template
const toggler = ref(null);
const dropdown = ref(null); // This is the root div
const dropdownEl = ref(null); // This is the dropdown__dropdown div

// Variables for internal use - matching original logic
let togglerEl = null;
let rootEl = null;
let dropdownContentEl = null;

const language = computed(() => store.getters.language);
const serviceSearchQuery = ref("");

const filteredServices = computed(() => {
  if (!props.dropdownServices) return [];
  return props.dropdownServices.filter((service) => {
    const title = service[`long_${language.value}`] || "";
    return title.toLowerCase().includes(serviceSearchQuery.value.toLowerCase());
  });
});

const computeDropdownLocation = () => {
  rect.value = togglerEl.getBoundingClientRect();
  x.value = rect.value.left + 36;
  y.value = rect.value.top;

  nextTick(() => {
    const dropdownRect = dropdownContentEl.getBoundingClientRect();

    isOutside.value = dropdownRect.right > window.innerWidth;

    if (isOutside.value) {
      x.value = rect.value.left - dropdownRect.width - 16;
    }
  });
};

const event = (e) => {
  const { target } = e;
  if (
    [...rootEl.querySelectorAll("*:not(button)")].includes(target) ||
    [...dropdownContentEl.querySelectorAll("*:not(button)")].includes(target)
  ) {
    showDropdown.value = true;
  } else {
    close();
  }
};

const close = () => {
  props.closeServicesDropdown();
  showDropdown.value = false;
};

watch(showDropdown, () => {
  nextTick(() => {
    computeDropdownLocation();
  });
});

onMounted(() => {
  togglerEl = toggler.value;
  rootEl = dropdown.value;
  dropdownContentEl = dropdownEl.value;
  document.addEventListener("click", event);
  document.body.append(dropdownContentEl);
  computeDropdownLocation();
});

onUnmounted(() => {
  document.removeEventListener("click", event);
  if (document.body.contains(dropdownContentEl)) {
    document.body.removeChild(dropdownContentEl);
  }
});
</script>

<style scoped lang="scss">
.dropdown {
  &__toggler {
    width: 24px;
    height: 24px;
    background: var(--bg-2);
    border-radius: var(--radius-3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:before {
      content: "";
      display: block;
      width: 15px;
      height: 15px;
      background: url("../assets/icons/default/add_icon.svg") no-repeat center;
      background-size: 15px;
      transition: all 0.3s ease;
    }

    &--active:before,
    &:hover:before {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: fixed;
    z-index: 2;
    min-height: 100px;
    background: var(--bg-1);
    padding: 10px 20px;
    border: 1px solid var(--border-2);
    border-radius: var(--radius-1);
    box-shadow: 0px 3px 30px var(--shadow-4);
    width: 300px;
  }

  &__title {
    color: var(--text-5);
    margin-bottom: 15px;
  }

  &__search {
    margin-bottom: 15px;
  }

  &__item {
    display: flex;
    align-items: center;
    font-size: var(--x-small);
    color: var(--text-2);
    padding: 6px 0;
  }

  &__item-title {
    margin-left: 10px;
  }

  &__content {
    position: relative;
  }

  &__body {
    max-height: 150px;
    overflow: auto;
    margin-bottom: 10px;
  }

  &__buttons {
    display: flex;
    justify-content: flex-end;
  }

  &__button {
    width: 44px;
    height: 27px;
    border-radius: var(--radius-1);

    &--submit {
      background-color: var(--bg-2);
      margin-right: 20px;
      background-image: url("../assets/icons/default/tick_icon.svg");
    }

    &--cancel {
      background: var(--bg-12);
      background-image: url("../assets/icons/default/x_icon_white.svg");
    }

    &--submit,
    &--cancel {
      background-repeat: no-repeat;
      background-size: 15px;
      background-position: center;
    }
  }
}

.dropdown__search {
  input {
    width: 100%;
  }

  input::placeholder{
    color: var(--text-2) !important;
  }
}

.dropdown__dropdown {
  width: 400px;
}
</style>
